import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Stack,
  Tooltip,
  Divider,
  useTheme,
  alpha,
  Card,
  Button,
  Checkbox,
  Alert,
} from '@mui/material';
import _ from 'lodash';

// Icons
import {
  Launch as LaunchIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Description as DescriptionIcon,
  AccountBalance as AccountBalanceIcon,
  Paid as PaidIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  CallSplit as CallSplitIcon,
  ImageSearch as ImageSearchIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

const paymentStatusMap = {
  PENDING: { label: 'รอจ่ายเงิน', color: 'warning' },
  PARTIAL: { label: 'จ่ายบางส่วน', color: 'info' },
  SUCCESS: { label: 'จ่ายแล้ว', color: 'success' },
  pending: { label: 'รอดำเนินการ', color: 'warning' },
  approved: { label: 'อนุมัติแล้ว', color: 'success' },
  paid: { label: 'จ่ายแล้ว', color: 'info' },
};

// Row component แสดงรายการเบิกจ่าย
function PaymentRow({
  payment,
  index,
  handleOnclickDetail,
  onClickDelete,
  onCompletePayment,
  onPrintPayment,
  onSplitPayment,
  onEditSlip,
}) {
  const [open, setOpen] = useState(false);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [copiedField, setCopiedField] = useState(null);
  const theme = useTheme();

  const handleCopy = (field, value) => (e) => {
    e.stopPropagation();
    if (!value) return;
    navigator.clipboard.writeText(String(value)).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField((prev) => (prev === field ? null : prev)), 1500);
    });
  };

  const status = payment?.status || 'PENDING';
  const statusConfig = paymentStatusMap[status] || paymentStatusMap.PENDING;
  const canComplete = status !== 'SUCCESS' && typeof onCompletePayment === 'function';
  const canPrint = typeof onPrintPayment === 'function';
  const canSplit = status === 'PENDING' && typeof onSplitPayment === 'function';
  const canEditSlip = status === 'SUCCESS' && typeof onEditSlip === 'function';

  const handleToggleExpense = (expensesId) => {
    setSelectedExpenses((prev) =>
      (prev.includes(expensesId) ? prev.filter((id) => id !== expensesId) : [...prev, expensesId]));
  };

  const handleSplit = () => {
    if (selectedExpenses.length === 0) return;
    onSplitPayment(payment, selectedExpenses);
    setSelectedExpenses([]);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format dates
  const formatDate = (dateString, showTime = false) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        ...(showTime && { hour: '2-digit', minute: '2-digit' }),
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <>
      <TableRow
        hover
        sx={{
          '&:nth-of-type(odd)': {
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          },
          cursor: 'pointer',
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.05),
          },
          height: 60,
        }}
      >
        {/* ลำดับ */}
        <TableCell
          align="center"
          width={60}
          onClick={() => handleOnclickDetail(payment?._id)}
        >
          <Typography variant="body2">{index + 1}</Typography>
        </TableCell>

        {/* เลขที่เอกสาร */}
        <TableCell onClick={() => handleOnclickDetail(payment?._id)}>
          <Typography variant="body2" fontWeight={500}>
            {payment?.payment_number || '-'}
          </Typography>
        </TableCell>

        {/* วันที่ขอรับ/จ่าย */}
        <TableCell onClick={() => handleOnclickDetail(payment?._id)}>
          <Stack spacing={0.5}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                ขอรับ
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatDate(payment?.dateRequest)}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                จ่าย
              </Typography>
              <Typography variant="body2">
                {formatDate(payment?.datePayment) || '-'}
              </Typography>
            </Box>
          </Stack>
        </TableCell>

        {/* วันที่ทำรายการ */}
        <TableCell onClick={() => handleOnclickDetail(payment._id)}>
          <Typography variant="body2">
            {formatDate(payment?.createdAt, true)}
          </Typography>
        </TableCell>

        {/* ประเภท */}
        <TableCell onClick={() => handleOnclickDetail(payment._id)}>
          <Chip
            size="small"
            label={payment?.pay_type?.name || payment?.pay_type || '-'}
            sx={{
              bgcolor: alpha(theme.palette.info.main, 0.1),
              color: 'info.main',
              fontWeight: 500,
              fontSize: '0.75rem',
            }}
          />
        </TableCell>

        {/* ผู้ขอเบิก */}
        <TableCell onClick={() => handleOnclickDetail(payment._id)}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonIcon fontSize="small" color="action" sx={{ opacity: 0.6 }} />
            <Typography variant="body2">
              {payment?.requester?.firstname
                ? `${payment?.requester?.firstname} ${
                    payment?.requester?.lastname || ''
                  }`
                : '-'}
            </Typography>
          </Stack>
        </TableCell>

        {/* ผู้รับเงิน/ธนาคาร */}
        <TableCell onClick={() => handleOnclickDetail(payment._id)}>
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={500}>
              {payment?.payee?.name || '-'}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <AccountBalanceIcon
                sx={{ fontSize: 14, color: 'text.secondary' }}
              />
              <Typography variant="caption" color="text.secondary">
                {payment?.payee?.bank || '-'}{' '}
                {payment?.payee?.account_number || ''}
              </Typography>
              {payment?.payee?.account_number && (
                <Tooltip title={copiedField === 'account' ? 'คัดลอกแล้ว!' : 'คัดลอกเลขบัญชี'}>
                  <IconButton
                    size="small"
                    onClick={handleCopy('account', payment?.payee?.account_number)}
                    sx={{ p: 0.25 }}
                  >
                    {copiedField === 'account' ? (
                      <CheckIcon sx={{ fontSize: 14 }} color="success" />
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </TableCell>

        {/* ยอดเงิน / สถานะ */}
        <TableCell onClick={() => handleOnclickDetail(payment._id)}>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={0.25} alignItems="center">
              <Typography variant="body2" fontWeight={600} color="success.main">
                {formatCurrency(
                  _.sumBy(payment?.expenses_list, (item) => (item?.price || 0) - (item?.tax || 0)) || payment?.totalAmount || 0
                )}
              </Typography>
              <Tooltip title={copiedField === 'amount' ? 'คัดลอกแล้ว!' : 'คัดลอกยอดเงิน'}>
                <IconButton
                  size="small"
                  onClick={handleCopy(
                    'amount',
                    (
                      _.sumBy(payment?.expenses_list, (item) => (item?.price || 0) - (item?.tax || 0)) || payment?.totalAmount || 0
                    ).toFixed(2)
                  )}
                  sx={{ p: 0.25 }}
                >
                  {copiedField === 'amount' ? (
                    <CheckIcon sx={{ fontSize: 14 }} color="success" />
                  ) : (
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
            <Chip
              size="small"
              label={statusConfig.label}
              color={statusConfig.color}
              sx={{ fontSize: '0.7rem' }}
            />
          </Stack>
        </TableCell>

        {/* รายละเอียด / จ่ายเงิน / ลบ */}
        <TableCell align="center">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={0.75}
            justifyContent="center"
            alignItems="center"
            sx={{ minWidth: { xs: 120, sm: 180 } }}
          >
            <Stack direction="row" spacing={0.25} justifyContent="center">
              <Tooltip title={open ? 'ซ่อนรายละเอียด' : 'ดูรายละเอียด'}>
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => setOpen(!open)}
                >
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </Tooltip>

              {canPrint && (
                <Tooltip title="พิมพ์ PDF">
                  <IconButton
                    aria-label="print pdf"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPrintPayment(payment);
                    }}
                  >
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {canEditSlip && (
                <Tooltip title="แก้ไขสลิปการโอน">
                  <IconButton
                    aria-label="edit slip"
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditSlip(payment);
                    }}
                  >
                    <ImageSearchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="ลบรายการ">
                <IconButton
                  aria-label="delete"
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickDelete(payment?._id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {canComplete && (
              <Tooltip title="จ่ายเงิน + อัปโหลดสลิป">
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  startIcon={<PaidIcon fontSize="small" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCompletePayment(payment);
                  }}
                  sx={{
                    borderRadius: 2,
                    px: 1.5,
                    minWidth: { xs: 120, sm: 110 },
                    width: { xs: '100%', sm: 'auto' },
                    fontWeight: 700,
                    textTransform: 'none',
                  }}
                >
                  จ่ายเงิน
                </Button>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {/* Collapsible section สำหรับแสดงรายละเอียดเพิ่มเติม */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                py: 2,
                px: 3,
                bgcolor: alpha(theme.palette.background.default, 0.7),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  รายการเบิกจ่าย
                </Typography>
                {canSplit && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'info.main', bgcolor: alpha(theme.palette.info.main, 0.1), px: 1.5, py: 0.5, borderRadius: 1 }}>
                    <CallSplitIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption">
                      <strong>คำแนะนำ:</strong> หากต้องการแยกจ่ายบางรายการ ให้ติ๊กเลือกช่องด้านหน้าที่ต้องการ แล้วกดปุ่ม "แยกจ่าย"
                    </Typography>
                  </Box>
                )}
              </Box>

              {(() => {
                const slips = payment?.slips || [];
                const legacySlip = payment?.slip;
                const legacyInSlips = slips.some((s) => s?._id === legacySlip?._id);
                const allSlips = [
                  ...slips,
                  ...(legacySlip && !legacyInSlips ? [legacySlip] : []),
                ].filter(Boolean);
                return allSlips.length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                      สลิปการโอน ({allSlips.length} รูป)
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {allSlips.map((slip, i) => (
                        <Box
                          key={slip?._id || i}
                          component="img"
                          src={slip?.url || slip?.src}
                          alt={`สลิป ${i + 1}`}
                          onClick={() => { const u = slip?.url || slip?.src; if (u) window.open(u, '_blank'); }}
                          sx={{ width: 90, height: 90, objectFit: 'cover', border: 1, borderColor: 'divider', borderRadius: 1, cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : null;
              })()}
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mt: 2 }}
              >
                {canSplit && selectedExpenses.length > 0 && (
                  <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<CallSplitIcon />}
                      onClick={handleSplit}
                    >
                      แยกจ่าย ({selectedExpenses.length} รายการ)
                    </Button>
                  </Box>
                )}
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      {canSplit && <TableCell width={40} padding="checkbox" />}
                      <TableCell align="center" width={50}>
                        ลำดับ
                      </TableCell>
                      <TableCell>รายการ</TableCell>
                      <TableCell>งบประมาณ/โครงการ</TableCell>
                      <TableCell align="right">จำนวนเงิน</TableCell>
                      <TableCell align="right">VAT</TableCell>
                      <TableCell align="right">หัก ณ ที่จ่าย</TableCell>
                      <TableCell align="right">ยอดรวม</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payment?.expenses_list?.map((item, index) => {
                      const expId = String(item?.expenses_id || item?._id || index);
                      const isChecked = selectedExpenses.includes(expId);
                      return (
                      <TableRow
                        key={index}
                        selected={isChecked}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          cursor: canSplit ? 'pointer' : 'default',
                        }}
                        onClick={canSplit ? () => handleToggleExpense(expId) : undefined}
                      >
                        {canSplit && (
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              onChange={() => handleToggleExpense(expId)}
                            />
                          </TableCell>
                        )}
                        <TableCell align="center">{index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item?.name || '-'}
                          </Typography>
                          {item?.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {item.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            {item?.budget && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <AccountBalanceIcon
                                  sx={{ fontSize: 14, color: 'text.secondary' }}
                                />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {item?.budget?.prefix}{' '}
                                  {item?.budget?.budget_number}
                                </Typography>
                              </Box>
                            )}
                            {item?.project && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <DescriptionIcon
                                  sx={{ fontSize: 14, color: 'text.secondary' }}
                                />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {item?.project?.project_number}
                                </Typography>
                              </Box>
                            )}
                            {!item?.budget && !item?.project && '-'}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(item?.price || 0)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: item?.vat ? 'error.main' : 'inherit' }}
                        >
                          {item?.vat ? `+${formatCurrency(item?.vat)}` : '-'}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: item?.tax ? 'primary.main' : 'inherit' }}
                        >
                          {item?.tax ? `-${formatCurrency(item?.tax)}` : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(item?.total || item?.price || 0)}
                        </TableCell>
                      </TableRow>
                    );
                    })}
                    {(!payment?.expenses_list ||
                      payment?.expenses_list.length === 0) && (
                      <TableRow>
                        <TableCell
                          colSpan={canSplit ? 8 : 7}
                          align="center"
                          sx={{ py: 2, color: 'text.secondary' }}
                        >
                          ไม่พบรายการ
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function PaymentListTable({
  payments,
  handleOnclickDetail,
  onClickDelete,
  onCompletePayment,
  onPrintPayment,
  onSplitPayment,
  onEditSlip,
  page,
  setPage,
  size,
  setSize,
}) {
  const theme = useTheme();

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setSize(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <Card elevation={0} variant="outlined">
      <TableContainer>
        <Table size="medium" aria-label="payment list table">
          <TableHead>
            <TableRow
              sx={{
                bgcolor: theme.palette.primary.main,
              }}
            >
              <TableCell align="center" width={60}>
                <Typography variant="subtitle2" fontWeight={600} color="white">
                  ลำดับ
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600} color="white">
                  เลขที่เอกสาร
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600} color="white">
                  วันที่ขอรับ/จ่าย
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600} color="white">
                  วันที่ทำรายการ
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600} color="white">
                  ประเภท
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600} color="white">
                  ผู้ขอเบิก
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600} color="white">
                  ผู้รับเงิน/ธนาคาร
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600} color="white">
                  ยอดเงิน/สถานะ
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600} color="white">
                  รายละเอียด
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {_.isEmpty(payments?.rows) ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      ไม่พบข้อมูลรายการเบิกจ่าย
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      ยังไม่มีรายการเบิกจ่ายในระบบ
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              _.map(payments?.rows, (payment, index) => (
                <PaymentRow
                  key={payment._id}
                  payment={payment}
                  index={index + (page - 1) * size}
                  handleOnclickDetail={handleOnclickDetail}
                  onClickDelete={onClickDelete}
                  onCompletePayment={onCompletePayment}
                  onPrintPayment={onPrintPayment}
                  onSplitPayment={onSplitPayment}
                  onEditSlip={onEditSlip}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={payments?.total || 0}
        rowsPerPage={size}
        page={page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="แสดง"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} จาก ${count !== -1 ? count : `มากกว่า ${to}`}`
        }
      />
    </Card>
  );
}
